import { saveCreationToDatabase, fetchCreationsFromDatabase, updateCreationName, deleteCreationFromDatabase, addMediaToCreation, deleteMediaFromCreation, fetchChildren } from './supabaseClient';

// Default child_id used when migrating legacy cache entries that predate multi-child support.
// Resolved lazily from the children table on first use. All pre-migration creations belong to Aiden.
let aidenIdCache = null;
const getAidenId = async () => {
  if (aidenIdCache) return aidenIdCache;
  const children = await fetchChildren();
  const aiden = children.find(c => c.name === 'aiden');
  if (!aiden) throw new Error('Aiden not found in children table');
  aidenIdCache = aiden.id;
  return aidenIdCache;
};

// Update sync status in UI
const updateSyncStatus = (status, message) => {
  const element = document.getElementById('sync-status');
  if (element) {
    element.className = `sync-status-notice ${status}`;
    element.innerHTML = `<p>${message}</p>`;
  }
};

// Cloudinary upload utility
export const uploadToCloudinary = async (file, metadata = {}) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  
  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary configuration missing');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', 'lego-creations');
  
  // Add metadata as tags and public_id for cross-device access
  if (metadata.creationId) {
    formData.append('tags', `creation-${metadata.creationId}`);
  }
  
  // Encode creation name in public_id for easy retrieval
  // Use Date.now() + random string to ensure uniqueness even for concurrent uploads
  if (metadata.creationName && metadata.creationId) {
    const safeName = metadata.creationName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const publicId = `lego-creations/${metadata.creationId}/${safeName}-${uniqueSuffix}`;
    formData.append('public_id', publicId);
  }
  
  if (metadata.creationName || metadata.dateAdded) {
    const context = {};
    if (metadata.creationName) context.creationName = metadata.creationName;
    if (metadata.dateAdded) context.dateAdded = metadata.dateAdded;
    formData.append('context', JSON.stringify(context));
  }

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Upload failed');
    }

    const data = await response.json();
    return {
      url: data.secure_url,
      publicId: data.public_id,
      width: data.width,
      height: data.height,
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

// Generate optimized image URLs
export const getOptimizedImageUrl = (publicId, options = {}) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const {
    width = 'auto',
    height = 'auto',
    quality = 'auto',
    format = 'auto',
  } = options;

  return `https://res.cloudinary.com/${cloudName}/image/upload/w_${width},h_${height},q_${quality},f_${format}/${publicId}`;
};

// Store creation metadata locally as backup
const CREATIONS_CACHE_KEY = 'cloudinary-creations-cache';

// Get cached creations
const getCachedCreations = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    console.log('Using cached creations:', cached.length);
    return cached.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
  } catch (error) {
    console.error('Error reading cached creations:', error);
    return [];
  }
};

// Tag any cached creations missing childId with Aiden's id (legacy cache from before multi-child support).
// Called on app load to prevent stale localStorage from breaking the child filter.
// On migration failure, clears the stale cache entirely so the next load fetches
// fresh from the database rather than silently serving entries that won't match
// any child filter (which would hide them from the gallery without any error).
export const migrateLegacyCacheIfNeeded = async () => {
  let cached;
  try {
    cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
  } catch (error) {
    console.error('Error reading cached creations — clearing corrupt cache:', error);
    localStorage.removeItem(CREATIONS_CACHE_KEY);
    return;
  }

  const hasLegacyEntries = cached.some(c => !c.childId);
  if (!hasLegacyEntries) return;

  try {
    const aidenId = await getAidenId();
    const migrated = cached.map(c => c.childId ? c : { ...c, childId: aidenId });
    localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(migrated));
    console.log(`Migrated ${cached.length} cached creations with Aiden childId`);
  } catch (error) {
    console.error('Error migrating legacy cache — clearing stale cache to force fresh DB fetch:', error);
    localStorage.removeItem(CREATIONS_CACHE_KEY);
  }
};

// Save creation metadata to database
export const saveCreationMetadata = async (creationData) => {
  try {
    // Save to database first (primary storage)
    await saveCreationToDatabase(creationData);
    console.log('✅ Saved creation to database:', creationData.name);
    
    // Also cache locally as backup
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    const existingIndex = cached.findIndex(c => c.id === creationData.id);
    
    if (existingIndex >= 0) {
      cached[existingIndex] = creationData;
    } else {
      cached.push(creationData);
    }
    
    localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(cached));
    console.log('✅ Also cached locally as backup');
  } catch (error) {
    console.error('Error saving creation metadata:', error);
    throw error;
  }
};

// Fetch creations from database with fallback to cache
export const fetchCreationsFromCloudinary = async () => {
  console.log('🔍 Loading creations from database...');
  
  try {
    // Try to fetch from database first
    console.log('🔍 Attempting to fetch from Supabase database...');
    const databaseCreations = await fetchCreationsFromDatabase();
    
    if (databaseCreations.length > 0) {
      console.log('✅ Found', databaseCreations.length, 'creations in database');
      updateSyncStatus('synced', '🌐 Cross-device sync active! All your creations sync across devices.');
      return databaseCreations;
    }
    console.log('📦 No creations in database yet');
    
    // If no database creations, check cache for migration
    const cachedCreations = getCachedCreations();
    if (cachedCreations.length > 0) {
      console.log('📦 Found cached creations, migrating to database...');
      // Resolve default childId once; all legacy creations belong to Aiden
      const aidenId = await getAidenId();
      // Migrate cached data to database
      for (const creation of cachedCreations) {
        try {
          await saveCreationToDatabase({ ...creation, childId: creation.childId || aidenId });
        } catch (error) {
          console.warn('Migration error for creation:', creation.name, error);
        }
      }
      updateSyncStatus('synced', '🌐 Migrated to database! Cross-device sync now active.');
      return cachedCreations;
    }
    
    console.log('📱 No creations found');
    updateSyncStatus('local-only', '📱 Upload your first creation to get started!');
    return [];
    
  } catch (error) {
    console.error('❌ Database error, using cached data:', error);
    updateSyncStatus('local-only', '📱 Database unavailable, using local data only.');
    return getCachedCreations();
  }
};

// Update creation name
export const updateCreationNameInCloud = async (creationId, newName) => {
  try {
    // Update in database
    await updateCreationName(creationId, newName);
    
    // Update local cache as well
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    const index = cached.findIndex(c => c.id === creationId);
    if (index >= 0) {
      cached[index].name = newName;
      localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(cached));
    }
    
    console.log('✅ Updated creation name in both database and cache');
  } catch (error) {
    console.error('Error updating creation name:', error);
    throw error;
  }
};

// Delete creation
export const deleteCreation = async (creationId) => {
  try {
    // Delete from database
    await deleteCreationFromDatabase(creationId);
    
    // Remove from local cache
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    const filtered = cached.filter(c => c.id !== creationId);
    localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(filtered));
    
    console.log('✅ Deleted creation from both database and cache');
  } catch (error) {
    console.error('Error deleting creation:', error);
    throw error;
  }
};

// Add media to existing creation
export const addMediaToExistingCreation = async (creationId, files) => {
  try {
    console.log('🔄 Adding media to existing creation:', { creationId, fileCount: files.length });
    
    // Upload files to Cloudinary
    const uploadPromises = files.map(async (file) => {
      // Only compress images, not videos
      const isVideo = file.type.startsWith('video/');
      const processedFile = isVideo ? file : await require('./imageUtils').compressImage(file);
      
      try {
        // Upload to Cloudinary with creation metadata
        const cloudinaryResponse = await uploadToCloudinary(processedFile, { creationId });
        
        return {
          url: cloudinaryResponse.url,
          publicId: cloudinaryResponse.publicId,
          name: file.name,
          width: cloudinaryResponse.width,
          height: cloudinaryResponse.height,
          mediaType: isVideo ? 'video' : 'image'
        };
      } catch (uploadError) {
        console.error('Failed to upload to Cloudinary:', uploadError);
        throw uploadError;
      }
    });
    
    const uploadedMedia = await Promise.all(uploadPromises);
    
    // Save to database
    await addMediaToCreation(creationId, uploadedMedia);
    
    // Update local cache
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    const creationIndex = cached.findIndex(c => c.id === creationId);
    if (creationIndex >= 0) {
      cached[creationIndex].photos = [...cached[creationIndex].photos, ...uploadedMedia];
      localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(cached));
    }
    
    console.log('✅ Added media to creation and updated cache');
    return uploadedMedia;
  } catch (error) {
    console.error('Error adding media to creation:', error);
    throw error;
  }
};

// Delete media from creation
export const removeMediaFromCreation = async (creationId, mediaUrl, publicId) => {
  try {
    console.log('🔄 Removing media from creation:', { creationId, mediaUrl });
    
    // Delete from database first
    await deleteMediaFromCreation(creationId, mediaUrl);
    
    // Update local cache
    const cached = JSON.parse(localStorage.getItem(CREATIONS_CACHE_KEY) || '[]');
    const creationIndex = cached.findIndex(c => c.id === creationId);
    if (creationIndex >= 0) {
      cached[creationIndex].photos = cached[creationIndex].photos.filter(photo => photo.url !== mediaUrl);
      localStorage.setItem(CREATIONS_CACHE_KEY, JSON.stringify(cached));
    }
    
    // TODO: Delete from Cloudinary if we have public_id
    // This would require Cloudinary destroy API which needs server-side implementation
    // For now, we'll just remove from database/cache
    
    console.log('✅ Removed media from creation and updated cache');
  } catch (error) {
    console.error('Error removing media from creation:', error);
    throw error;
  }
};